import { Injectable } from '@angular/core';
import { Observable, from, throwError } from 'rxjs';
import { catchError, map, tap, timeout } from 'rxjs/operators';

import { ProjektService } from '../projekt.service';
import { NetworkStatusService, TimeoutFehler } from './network-status.service';
import { OfflineDbService } from './offline-db.service';
import { OfflineSyncService } from './offline-sync.service';

import { Projekt, ProjektDetail } from '../../Models/projekt.model';
import { DatenErgebnis } from '../../Models/offline.model';

/**
 * Projektübersicht und Projektseite – online vom Server, ohne Verbindung aus
 * dem lokalen Bestand der heruntergeladenen Projekte.
 *
 * Damit lässt sich ein heruntergeladenes Projekt über denselben Weg öffnen
 * wie online: Projektübersicht → Projekt → Listen. Die Navigation bleibt
 * gleich; nur die Datenquelle wechselt. Der Serverweg ist ein reiner Strom,
 * erst ein Netzwerkfehler führt in die IndexedDB.
 */
@Injectable({ providedIn: 'root' })
export class ProjektDataService {

  private static readonly LESE_TIMEOUT_MS = 12_000;

  constructor(
    private api: ProjektService,
    private db: OfflineDbService,
    private network: NetworkStatusService,
    private sync: OfflineSyncService
  ) { }

  /** Alle Projekte – offline nur die heruntergeladenen. */
  getProjekte(): Observable<DatenErgebnis<Projekt[]>> {
    if (!this.network.lohntVersuch()) {
      return from(this.liesProjekteLokal());
    }

    return this.api.getProjekte().pipe(
      this.mitTimeout(),
      tap(() => this.network.meldeErfolg()),
      map(projekte => ({ daten: projekte ?? [], quelle: 'server' as const })),
      catchError(fehler => {
        this.behandleFehler(fehler);

        if (!NetworkStatusService.istNetzwerkfehler(fehler)) {
          return throwError(() => fehler);
        }

        return from(this.liesProjekteLokal());
      })
    );
  }

  private async liesProjekteLokal(): Promise<DatenErgebnis<Projekt[]>> {
    const lokal = await this.db.ladeProjekte();

    return {
      daten: lokal
        .map(p => p.projekt as Projekt)
        .sort((a, b) => a.name.localeCompare(b.name, 'de')),
      quelle: 'offline',
      standUtc: this.juengsterStand(lokal.map(p => p.downloadedAtUtc))
    };
  }

  /** Die Projektseite mit Inbetriebnahmen, Fortschritt und Anlagenteilen. */
  getProjekt(id: number): Observable<DatenErgebnis<ProjektDetail>> {
    if (!this.network.lohntVersuch()) {
      return from(this.liesProjektLokal(id));
    }

    return this.api.getProjekt(id).pipe(
      this.mitTimeout(),
      tap(projekt => {
        this.network.meldeErfolg();

        // Nebenwirkung: die Projektseite im lokalen Bestand nachziehen – sie
        // enthält nur Kennzahlen, keine vor Ort erfassten Daten.
        void this.aktualisiereLokal(id, projekt);
      }),
      map(projekt => ({ daten: projekt, quelle: 'server' as const })),
      catchError(fehler => {
        this.behandleFehler(fehler);

        if (!NetworkStatusService.istNetzwerkfehler(fehler)) {
          return throwError(() => fehler);
        }

        return from(this.liesProjektLokal(id, fehler));
      })
    );
  }

  private async liesProjektLokal(id: number, urspruenglicherFehler?: unknown): Promise<DatenErgebnis<ProjektDetail>> {
    const lokal = await this.db.ladeProjekt(id);

    if (!lokal) {
      if (urspruenglicherFehler) {
        throw urspruenglicherFehler;
      }

      throw new Error(
        'Keine Verbindung zum Server. Dieses Projekt wurde nicht für die Offline-Nutzung heruntergeladen.'
      );
    }

    // Beim Öffnen eines offline gespeicherten Projekts ist ein guter Moment,
    // Liegengebliebenes loszuwerden.
    void this.sync.synchronisiere('geoeffnet');

    return {
      daten: { ...lokal.projekt, inbetriebnahmen: lokal.projekt.inbetriebnahmen ?? [] },
      quelle: 'offline',
      standUtc: lokal.downloadedAtUtc
    };
  }

  private async aktualisiereLokal(projektId: number, projekt: ProjektDetail): Promise<void> {
    try {
      if (!(await this.db.ladeProjekt(projektId))) {
        return;
      }

      await this.db.db.projekte.update(projektId, {
        projekt,
        name: projekt.name,
        projektNummer: projekt.projektNummer,
        customerName: projekt.customerName ?? null
      });
    } catch (fehler) {
      console.error('Die lokale Projektseite konnte nicht nachgezogen werden.', fehler);
    }
  }

  private mitTimeout<T>() {
    return timeout<T, Observable<never>>({
      each: ProjektDataService.LESE_TIMEOUT_MS,
      with: () => throwError(() => new TimeoutFehler(ProjektDataService.LESE_TIMEOUT_MS))
    });
  }

  private behandleFehler(fehler: unknown): void {
    if (NetworkStatusService.istNetzwerkfehler(fehler)) {
      this.network.meldeNetzwerkfehler();
    }
  }

  private juengsterStand(staende: string[]): string | null {
    const sortiert = staende.filter(Boolean).sort();
    return sortiert.length > 0 ? sortiert[sortiert.length - 1] : null;
  }
}
